/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Box, Flex, Tabs, rem, Text, useMantineTheme } from '@mantine/core';
import { Suspense, type ReactNode } from 'react';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';

import i18n from '@fai/friendli-suite/features/i18n';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { omitDefaultPaths } from '@fai/friendli-suite/features/routes/paths/default-paths';
import { GlobalHeaderMessage } from '@fai/friendli-suite/views/layout/header-messages';
import { TextVariants } from '@fai/ui/components/typography/variants';

const tabLabels: Record<string, ReactNode> = {
  billing: i18n.t('team_settings.billing'),
  members: i18n.t('team_settings.members'),
  settings: i18n.t('team_settings.team_settings'),
};

export default function TeamSettingsLayout() {
  const navigate = useNavigate();
  const { teamId = '' } = useParams();
  const theme = useMantineTheme();

  const teamSettingsBasePath = Paths.team(teamId).teamSettings;
  const tabValues = omitDefaultPaths(Object.keys(teamSettingsBasePath));

  const currentPath = useMatch(teamSettingsBasePath.getMenuPattern)?.params.current || '';

  const handleTabChange = (value: string | null) => {
    navigate(`${teamSettingsBasePath.default}/${value}`, {
      replace: true,
    });
  };

  return (
    <>
      <GlobalHeaderMessage />

      <Flex
        align="start"
        direction={{ base: 'column', lg: 'row' }}
        gap={theme.pf.layout.xl}
        m="0 auto"
        maw={rem(1320)} // 1240 + (padding = 40 * 2)
        p={{ base: theme.pf.layout.sm, lg: theme.pf.layout.xl }}
        w="100%"
        wrap="nowrap"
      >
        <Tabs orientation="vertical" value={currentPath} onChange={handleTabChange}>
          <Tabs.List w={{ base: '100%', lg: rem(240) }}>
            {tabValues.map((tabValue) => (
              <Tabs.Tab key={tabValue} value={tabValue}>
                <Text variant={TextVariants.TitleSm}>{tabLabels[tabValue]}</Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <Box w={{ base: '100%', lg: 'calc(100% - 17.5rem)' }}>
          <Suspense>
            <Outlet />
          </Suspense>
        </Box>
      </Flex>
    </>
  );
}
